import React, {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {useNavigate, useParams} from 'react-router-dom'
import {useAppSelector} from '../redux/hooks'
import useUserComplianceActions from '../hooks/useUserComplianceActions'
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator'
import {titleMarginBottom} from '../constants/layout'
import DetailsCard from '../components/DetailsCard'
import {formatDateOnlyLocale, formatDateTimeLocale} from '../utils/dateHelper'
import Button from '../components/Button'
import {AmlReportModel, ComplianceDecisionRequestModel, ComplianceStatus} from '../redux/companyMonitorTypes'
import NavLink from '../components/NavLink'
import CountryFlag from '../components/CountryFlag'
import {faArrowLeft} from '@fortawesome/free-solid-svg-icons'
import Alert from "react-bootstrap/Alert";
import Link from "../components/Link";
import {Badge} from "react-bootstrap";
import UserComplianceReportList from "../components/Aml/User/UserComplianceReportList";
import AmlReportModal from "../components/Aml/User/AmlReportModal";
import ComplianceDecisionModal from "../components/Aml/User/ComplianceDecisionModal";
import {getGenderFromSwedishSsn} from "../utils/identificationNumberHelper";


const UserComplianceDetails = () => {
  const styles = useStyles()
  const navigate = useNavigate()
  const {id} = useParams<{ id: string }>()

  const {getUserComplianceDetails, postUserComplianceDecision} = useUserComplianceActions()

  const loading = useAppSelector((state) => state.userCompliance.loading)
  const details = useAppSelector((state) => state.userCompliance.details)
  const error = useAppSelector((state) => state.userCompliance.error)

  const [visibleAmlReport, setVisibleAmlReport] = useState<AmlReportModel>()
  const [decisionModalVisible, setDecisionModalVisible] = useState(false)

  useEffect(() => {
    if (!details || (id && details.id !== id)) {
      if (id) {
        getUserComplianceDetails(id)
      }
    }
  }, [])

  const onComplianceDecisionCreated = (decision: ComplianceDecisionRequestModel) => {
    if (!id || !details || !details.complianceReports?.length) {
      return
    }

    const latestReport = details.complianceReports
      .slice() // avoid mutating original array
      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0]

    if (!latestReport) {
      return
    }

    postUserComplianceDecision(id, latestReport.id, decision)
    setDecisionModalVisible(false)
  }

  const renderComplianceAlert = (status: ComplianceStatus) => {
    switch (status) {
      case ComplianceStatus.compliant:
        return (
          <Alert style={{marginBottom: 0}} variant="success">
            <Alert.Heading>Compliance status: Compliant</Alert.Heading>
            <span>This user is currently assessed as compliant.</span>
          </Alert>
        )

      case ComplianceStatus.nonCompliant:
        return (
          <Alert style={{marginBottom: 0}} variant="danger">
            <Alert.Heading>Compliance status: Non-compliant</Alert.Heading>
            <span>This user is currently assessed as non-compliant.</span>
          </Alert>
        )

      case ComplianceStatus.unknown:
      default:
        return (
          <Alert style={{marginBottom: 0}} variant="warning">
            <Alert.Heading>Compliance status: Decision required</Alert.Heading>
            <span>
              The compliance status for this user is currently not decided.
              The latest compliance report indicates that the user may be non-compliant.
              A manual investigation and a decision is required.
            </span>
          </Alert>
        )
    }
  }

  if (!details || loading) {
    return (
      <div className={styles.container}>
        <h1>User compliance details</h1>
        <FullScreenLoadingIndicator visible={loading}/>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <h1>{details.givenName} {details.familyName}</h1>

      <div className={styles.actionBar}>
        <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)}/>
        <Button title={"Create Decision"} onClick={() => setDecisionModalVisible(true)}/>
      </div>

      {!!error && (
        <Alert variant='danger'>Error: {error}</Alert>
      )}

      {renderComplianceAlert(details.complianceStatus)}

      <div className={styles.section}>
        <DetailsCard title={"Identity"}>
          <p>User: <Link to={`/users/${details.id}`}>{details.givenName} {details.familyName}</Link></p>
          <p>Identification number: <span>{details.identificationNumber}</span></p>
          <p>Gender: <span>{getGenderFromSwedishSsn(details.identificationNumber)}</span></p>
          <p>Country: <span className={styles.uppercase}>
            {details.country ? <><CountryFlag country={details.country}/>{' '}{details.country}</> : '-'}
          </span></p>
          <p>Date of birth: <span>{formatDateOnlyLocale(details.dateOfBirth)}</span></p>
          <div className={styles.badgeList}>
            {!!details.deleted && <Badge bg={"secondary"}>Deleted</Badge>}
          </div>
        </DetailsCard>

        <DetailsCard title={"Timestamps"}>
          <p>Initial flag date: <span>{formatDateOnlyLocale(details.initialFlagDate)}</span></p>
          <p>AML subscription setup: <span>{formatDateTimeLocale(details.amlSubscriptionTimestamp)}</span></p>
          <p>Last AML report check: <span>{formatDateTimeLocale(details.amlReportTimestamp)}</span></p>
          <p>Last compliance report check: <span>{formatDateTimeLocale(details.complianceReportTimestamp)}</span></p>
        </DetailsCard>
      </div>

      <DetailsCard title={"Compliance reports"}>
        <UserComplianceReportList
          items={details.complianceReports}
          onOpenAmlReportClick={setVisibleAmlReport}/>
      </DetailsCard>

      <AmlReportModal
        title={"Aml report"}
        amlReport={visibleAmlReport}
        visible={!!visibleAmlReport}
        onCloseClick={() => setVisibleAmlReport(undefined)}
      />
      <ComplianceDecisionModal
        visible={decisionModalVisible}
        onComplianceDecisionCreated={onComplianceDecisionCreated}
        onCloseClick={() => setDecisionModalVisible(false)}
      />
    </div>
  )
}

export default UserComplianceDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom - 10
    },
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  section: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 10,
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  uppercase: {
    textTransform: 'uppercase',
  },
  badgeList: {
    display: 'flex',
    flexDirection: "row-reverse",
    flexWrap: 'wrap',
    gap: 10,
  },
})
